import { useCallback, useRef, useState } from "react";
import type { FieldValues, Path, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { useDispatch } from "react-redux";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import type { AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { fetchAdmissionPrefillPayloadByEmail } from "@/src/utils/tasks/fetchAdmissionPrefillPayloadByEmail";

interface UseAOPIdSourceFieldsOptions<TForm extends FieldValues> {
  setValue: UseFormSetValue<TForm>;
  getValues: UseFormGetValues<TForm>;
  applyTaskPrefill: (payload: AdmissionTaskPrefillPayload) => void;
  lastPrefilledTaskIdRef: React.MutableRefObject<string>;
  fieldEmail: boolean;
  userEmail?: string;
}

export const useAOPIdSourceFields = <TForm extends FieldValues>({
  setValue,
  getValues,
  applyTaskPrefill: applyTaskPrefillBase,
  lastPrefilledTaskIdRef,
  fieldEmail,
  userEmail,
}: UseAOPIdSourceFieldsOptions<TForm>) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const lastPrefilledStudentIdRef = useRef("");
  const [idSourceLock, setIdSourceLock] = useState<"task" | "student" | null>(null);

  const isTaskIdDisabled = !!fieldEmail || idSourceLock === "student";
  const isStudentIdDisabled = !!fieldEmail || idSourceLock === "task";
  /** Task/Student source selected → Field email toggle is locked until that source is cleared. */
  const isFieldEmailDisabled = idSourceLock === "task" || idSourceLock === "student";

  const handleClearField = useCallback(
    (fieldName: Path<TForm>) => {
      setValue(fieldName, "" as never, { shouldDirty: true });
    },
    [setValue],
  );

  const applyIdSourceLockFromPrefill = useCallback(
    (payload: AdmissionTaskPrefillPayload) => {
      const loadedTaskId = String(payload.task_id ?? "").trim();
      const loadedStudentId = String(payload.student_id ?? payload.applicant_id ?? "").trim();

      if (loadedTaskId) {
        setIdSourceLock("task");
        lastPrefilledStudentIdRef.current = loadedStudentId;
        if (loadedStudentId) {
          setValue("student_id" as Path<TForm>, loadedStudentId as never, {
            shouldDirty: false,
          });
        }
        return;
      }

      if (loadedStudentId) {
        setIdSourceLock("student");
        lastPrefilledTaskIdRef.current = "";
        setValue("student_id" as Path<TForm>, loadedStudentId as never, {
          shouldDirty: false,
        });
      }
    },
    [lastPrefilledTaskIdRef, setValue],
  );

  /** Prefer this for Task/Student/Email inputs so lock always runs (not only via onPrefill ref). */
  const applyTaskPrefill = useCallback(
    (payload: AdmissionTaskPrefillPayload) => {
      applyTaskPrefillBase(payload);
      applyIdSourceLockFromPrefill(payload);
    },
    [applyIdSourceLockFromPrefill, applyTaskPrefillBase],
  );

  const handleClearTaskId = useCallback(() => {
    handleClearField("task_id" as Path<TForm>);
    lastPrefilledTaskIdRef.current = "";
    if (idSourceLock === "task") {
      setIdSourceLock(null);
      lastPrefilledStudentIdRef.current = "";
      handleClearField("student_id" as Path<TForm>);
    }
  }, [handleClearField, idSourceLock, lastPrefilledTaskIdRef]);

  const handleClearStudentId = useCallback(() => {
    handleClearField("student_id" as Path<TForm>);
    lastPrefilledStudentIdRef.current = "";
    if (idSourceLock === "student") {
      setIdSourceLock(null);
      lastPrefilledTaskIdRef.current = "";
    }
  }, [handleClearField, idSourceLock, lastPrefilledTaskIdRef]);

  const handleClearUserEmail = useCallback(() => {
    handleClearField("userEmail" as Path<TForm>);
    if (idSourceLock === "student") {
      setIdSourceLock(null);
      lastPrefilledStudentIdRef.current = "";
      lastPrefilledTaskIdRef.current = "";
      handleClearField("student_id" as Path<TForm>);
      handleClearField("task_id" as Path<TForm>);
    }
  }, [handleClearField, idSourceLock, lastPrefilledTaskIdRef]);

  const handleCheckUserEmail = useCallback(async () => {
    const email = (userEmail ?? "").trim();
    if (!email) {
      notify({
        title: "Email is empty",
        message: "Enter an email to check.",
        type: "error",
      });
      return;
    }

    dispatch(setIsPreloyder(true));
    try {
      const payload = await fetchAdmissionPrefillPayloadByEmail(email);
      if (!payload) {
        notify({
          title: "User not found",
          message: `No user found with email "${email}".`,
          type: "error",
        });
        return;
      }

      applyTaskPrefill(payload);
      const loadedStudentId = String(payload.student_id ?? payload.applicant_id ?? "").trim();
      if (loadedStudentId) {
        lastPrefilledStudentIdRef.current = loadedStudentId;
      }
      lastPrefilledTaskIdRef.current = "";
      setIdSourceLock("student");

      notify({
        title: "User loaded",
        message: "Form fields were prefilled from the user. A task will be created on Generate.",
        type: "success",
      });
    } catch (error) {
      console.error("[AdmissionOffice] Email lookup failed:", error);
      notify({
        title: "User lookup failed",
        message: "Could not load user data by email. Please try again.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [applyTaskPrefill, dispatch, lastPrefilledTaskIdRef, notify, userEmail]);

  const handleCopyIdField = useCallback(
    async (fieldName: Path<TForm>) => {
      const value = getValues(fieldName);
      if (!value) return;

      try {
        await navigator.clipboard.writeText(String(value));
      } catch (err) {
        console.error("Ошибка копирования", err);
      }
    },
    [getValues],
  );

  return {
    applyTaskPrefill,
    applyIdSourceLockFromPrefill,
    lastPrefilledStudentIdRef,
    idSourceLock,
    isTaskIdDisabled,
    isStudentIdDisabled,
    isFieldEmailDisabled,
    handleClearTaskId,
    handleClearStudentId,
    handleClearUserEmail,
    handleCheckUserEmail,
    handleCopyIdField,
  };
};
